import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  Tooltip,
  useOutsideClick,
} from '@chakra-ui/react';
import { CheckIcon } from '@spotoninc/nexus-web-assets';
import { ChevronDownIcon, SearchIcon } from '@chakra-ui/icons';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWithAuth } from '../../utils/apiHelpers';
import {
  setCurrency,
  setIntegration,
  setLocationData,
} from '../../redux/slices/locationSlice';
import {
  setCompanySwitcherActive,
  setPeriodData,
  setPeriodFrom,
  setPeriodTo,
  setPeriodSwitcher,
  setLastFetchedIntegration,
  setDefaultDateValue,
} from '../../redux/slices/dateSlice';
import { addEventProperties } from '../../utils/analytics/heapAnalytics';
import { updateIntegrationConnectionStatus } from '../widgets/settings/integrationUtils';

const CustomLocationSelector = React.memo(
  ({
    locationValue,
    setLocation,
    multiple = false,
    label = null,
    height = '44px',
    isDisabled = false,
  }) => {
    // State hooks
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedValues, setSelectedValues] = useState(null);
    // Ref for outside click detection
    const ref = React.useRef();
    // Track in-flight period fetch to avoid duplicate requests for same integration
    const periodRequestRef = React.useRef(null);

    // Close dropdown when clicking outside
    useOutsideClick({
      ref: ref,
      handler: () => setIsOpen(false),
    });

    // Redux hooks
    const dispatch = useDispatch();
    const locationData = useSelector(
      state => state.locationSelectFormat?.locationData || []
    );
    const periodFrom = useSelector(state => state.dateFormat?.periodFrom || '');
    const defaultDateValue = useSelector(
      state => state.dateFormat?.defaultDateValue || false
    );
    const periodSwitcher = useSelector(
      state => state.dateFormat?.periodSwitcher || false
    );
    const periodSelect = useSelector(
      state => state.dateFormat?.periodSelect || false
    );
    const integration = useSelector(
      state => state.locationSelectFormat?.integration || null
    );
    const periodDataFetched = useSelector(
      state => state.dateFormat?.periodDataFetched || false
    );
    const lastFetchedIntegration = useSelector(
      state => state.dateFormat?.lastFetchedIntegration || null
    );
    const userId = useSelector(state => state.user?.userId || null);

    // Filter options based on search
    const filteredOptions = useMemo(() => {
      if (!locationData || !Array.isArray(locationData)) {
        return [];
      }
      if (searchValue.trim() === '') {
        return locationData;
      }
      return locationData.filter(item =>
        item.label.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [searchValue, locationData]);

    // Find location by value
    const findLocation = useCallback(
      location => locationData?.find(data => data.value === location),
      [locationData]
    );

    // Get selected location label(s)
    const selectedLocationLabel = useMemo(() => {
      if (multiple) {
        if (!selectedValues || selectedValues.length === 0) {
          return 'Select locations';
        }

        if (selectedValues.length === 1) {
          const location = findLocation(selectedValues[0]);
          return location ? location.label : 'Select locations';
        }

        return `${selectedValues.length} locations selected`;
      } else {
        const location = findLocation(locationValue);
        return location ? location.label : 'Select location';
      }
    }, [locationValue, findLocation, multiple, selectedValues]);

    // Helper functions
    const fetchPeriod = useCallback(() => {
      if (periodDataFetched && integration === lastFetchedIntegration) return;

      // Prevent duplicate in-flight requests for the same integration
      if (periodRequestRef.current === integration) return;
      periodRequestRef.current = integration;

      const controller = new AbortController();

      dispatch(setLastFetchedIntegration(integration));

      fetchWithAuth(`/api/fetch_periods/${integration ?? 'null'}`, {
        method: 'GET',
        signal: controller.signal,
      })
        .then(response => {
          if (!response.ok) throw new Error('Network response was not ok');
          return response.json();
        })
        .then(data => {
          if (data.code === undefined) {
            if (data.period_cal === 'true') {
              const dataNew = data.period_data.map(item => ({
                label: item.period_label,
                value: item.period_label,
              }));

              dispatch(setPeriodData(dataNew));
              dispatch(setPeriodSwitcher(true)); // Enable period switcher when periods are available
              if (periodFrom === '') {
                dispatch(setPeriodFrom(data.from_period));
                dispatch(setPeriodTo(data.to_period));
              }
            } else {
              dispatch(setPeriodSwitcher(false));
            }

            dispatch(
              setCompanySwitcherActive(data.company_period_cal === 'true')
            );
          } else {
            window.open('/', '_self');
            alert('Session Expired!.');
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Error fetching period data:', err);
          }
        })
        .finally(() => {
          periodRequestRef.current = null;
        });

      return () => controller.abort();
    }, [
      integration,
      periodFrom,
      dispatch,
      periodDataFetched,
      lastFetchedIntegration,
    ]);

    // Handle location or integration selection
    const handleLocationSelect = useCallback(
      val => {
        if (val === undefined) return;

        if (!multiple) {
          try {
            const selected = findLocation(val);
            if (!selected) return;

            // If integration-level selection (may have no locations)
            if (selected.isIntegration) {
              dispatch(setIntegration(selected.integrationId));
              // Don't reset period-related state here - let fetchPeriod handle it
              // This allows the date range to persist when switching locations in the same mode
              // Defer to allow integration state to update before fetching periods
              setTimeout(() => fetchPeriod(), 10);
              return;
            }

            // Otherwise, a specific location was selected
            dispatch(setCurrency(selected.currency));

            const isPeriodSwitcherEnabled =
              selected.ddl_value.split('|')[3].split('=')[1] !== 'false';

            // Only reset date/period values if we're switching between modes
            // If staying in the same mode, keep the current date range
            const isSwitchingModes = periodSwitcher !== isPeriodSwitcherEnabled;

            if (isPeriodSwitcherEnabled) {
              dispatch(setPeriodSwitcher(true));
              dispatch(setIntegration(selected.ddl_value.split('|')[1]));
              // Only reset date values if we're switching from date mode to period mode
              if (isSwitchingModes) {
                dispatch(setDefaultDateValue(undefined));
              }
              // Defer to allow integration state to update before fetching periods
              setTimeout(() => fetchPeriod(), 10);
            } else {
              dispatch(setPeriodSwitcher(false));
              // Only reset period values if we're switching from period mode to date mode
              if (isSwitchingModes) {
                dispatch(setPeriodFrom(''));
                dispatch(setPeriodTo(''));
              }
            }
          } finally {
            setIsOpen(false);
          }
        }
      },
      [multiple, findLocation, dispatch, fetchPeriod, periodSwitcher]
    );

    // Fetch locations + integrations for the reskinned overview dropdown
    const fetchLocations = useCallback(() => {
      if (!locationData || locationData.length === 0) {
        const controller = new AbortController();
        const signal = controller.signal;

        const locationsReq = fetchWithAuth('/api/fetch_locations/', {
          method: 'POST',
          signal,
        });
        const integrationsReq = fetchWithAuth('/api/auth_update/', {
          method: 'GET',
          signal,
        });
        // Fetch auth data to determine the current user's privilege id
        const authReq = fetchWithAuth('/api/fetch_auth_data/', {
          method: 'GET',
          signal,
        });

        Promise.all([locationsReq, integrationsReq, authReq])
          .then(async ([locRes, intRes, authRes]) => {
            if (!locRes.ok) throw new Error('Failed to fetch locations');
            if (!intRes.ok) throw new Error('Failed to fetch integrations');
            if (!authRes.ok) throw new Error('Failed to fetch auth data');

            const [locData, intData, authData] = await Promise.all([
              locRes.json(),
              intRes.json(),
              authRes.json(),
            ]);

            // Update Heap event properties with integration connection status
            updateIntegrationConnectionStatus(
              intData.integration,
              addEventProperties
            );

            // Determine current user and privilege id from authData.users
            const currentUserId =
              userId ??
              Number(window?.localStorage?.getItem('currentUserId') || 0);
            const currentEmail =
              (typeof window !== 'undefined' &&
              window.localStorage?.getItem('userInfo')
                ? (() => {
                    try {
                      return JSON.parse(window.localStorage.getItem('userInfo'))
                        .email;
                    } catch (_) {
                      return null;
                    }
                  })()
                : null) || null;

            const idEq = (a, b) => String(a) === String(b);
            const emailEq = (a, b) =>
              String(a || '').toLowerCase() === String(b || '').toLowerCase();

            const me = Array.isArray(authData?.users)
              ? authData.users.find(
                  u =>
                    idEq(u.user_id_id, currentUserId) ||
                    idEq(u.user_id, currentUserId) ||
                    idEq(u.id, currentUserId) ||
                    (currentEmail &&
                      (emailEq(u.email, currentEmail) ||
                        emailEq(u.email_id, currentEmail) ||
                        emailEq(u.emailId, currentEmail)))
                )
              : undefined;

            const directPrivId =
              me?.priviledge_id_id ||
              me?.privilege_id_id ||
              me?.priviledge_id ||
              me?.privilege_id ||
              me?.priv_id;

            // If direct privilege id is not present in users payload, resolve by matching
            // the user's priv_description to the priviledges list's label
            let resolvedPrivId = directPrivId;
            if (
              !resolvedPrivId &&
              me?.priv_description &&
              Array.isArray(authData?.priviledges)
            ) {
              const targetLabel = String(me.priv_description)
                .toLowerCase()
                .trim();
              const match = authData.priviledges.find(
                p =>
                  String(p?.label || '')
                    .toLowerCase()
                    .trim() === targetLabel
              );
              if (match && match.value !== undefined && match.value !== null) {
                resolvedPrivId = match.value;
              }
            }

            // If we have a privilege, fetch its assigned integrations
            let assignedIntegrationIds = new Set();
            const locationCountByIntegration = new Map();
            if (resolvedPrivId) {
              try {
                const privRes = await fetchWithAuth(
                  `/api/get_priv_data/${resolvedPrivId}/`,
                  {
                    method: 'GET',
                    signal,
                  }
                );
                if (privRes.ok) {
                  const privJson = await privRes.json();
                  const assigned = privJson?.assigned_integrations || [];
                  // Normalize assigned_integrations which can be primitives or objects
                  const normalizedAssigned = (assigned || [])
                    .map(item => {
                      if (item && typeof item === 'object') {
                        const raw =
                          'integration_id_id' in item
                            ? item.integration_id_id
                            : 'integration_id' in item
                              ? item.integration_id
                              : null;
                        return raw;
                      }
                      return item;
                    })
                    .filter(v => v !== null && v !== undefined)
                    .map(v => String(v));
                  assignedIntegrationIds = new Set(normalizedAssigned);

                  // Count locations per integration to determine which integration-level
                  // entries to show. If an integration has exactly 1 location, hide the
                  // integration-level entry (it would show duplicate data)
                  Object.keys(privJson)
                    .filter(key => !isNaN(key)) // Get numeric keys (integration IDs)
                    .forEach(intId => {
                      if (Array.isArray(privJson[intId])) {
                        const count = privJson[intId].filter(
                          loc => loc.assigned === true
                        ).length;
                        locationCountByIntegration.set(String(intId), count);
                      }
                    });
                }
              } catch (e) {
                // Swallow and fallback to showing only integrations that appear in locations
              }
            }

            // Normalize location entries and capture integrationId from ddl_value
            const locationItems = Array.isArray(locData)
              ? locData.map(item => ({
                  label: item.ui_label,
                  // Backend expects ui_label, then looks up ddl_value from it
                  value: item.ui_label,
                  currency: item.currency,
                  ddl_value: item.ddl_value,
                  period_length: item.period_length,
                  // Capture location_id_id from API response for filtering
                  locationId: item.location_id_id,
                  // Extract integration id from ddl_value (format: <...>|<integrationId>|...)
                  integrationId:
                    typeof item.ddl_value === 'string'
                      ? item.ddl_value.split('|')[1]
                      : null,
                }))
              : [];

            // Normalize integration entries from auth_update (include those with no locations)
            const allIntegrations = Array.isArray(intData?.integration)
              ? intData.integration
              : [];
            let integrationItems = allIntegrations.map(intg => ({
              label: intg.app_name || String(intg.id),
              value: `INTEGRATION::${intg.id}`,
              isIntegration: true,
              integrationId: String(intg.id),
              location_attr: !!intg.location_attr,
            }));

            // Determine integrations present via locations (always allowed)
            const allowedFromLocations = new Set(
              locationItems
                .map(x => x.integrationId)
                .filter(id => id !== null && id !== undefined)
            );

            // Filter integrations to those the user has access to
            // Allow if either explicitly assigned at integration level OR there are any locations for that integration
            if (assignedIntegrationIds.size > 0) {
              integrationItems = integrationItems.filter(
                item =>
                  assignedIntegrationIds.has(item.integrationId) ||
                  allowedFromLocations.has(item.integrationId)
              );
            } else {
              // Fallback: limit to integrations present in the locations list
              integrationItems = integrationItems.filter(item =>
                allowedFromLocations.has(item.integrationId)
              );
            }

            // Filter out integration-level entries for integrations with exactly 1 location
            // since they would show the same data as the single location
            integrationItems = integrationItems.filter(item => {
              const locationCount = locationCountByIntegration.get(
                item.integrationId
              );
              // Show if: no count data (fallback), 0 locations, or 2+ locations
              // Hide if exactly 1 location (would show duplicate data)
              return locationCount === undefined || locationCount !== 1;
            });

            // Helper function to normalize strings for comparison (remove periods, spaces, lowercase)
            const normalizeString = str =>
              str.toLowerCase().replace(/[.\s]/g, '');

            // Create a map of integrationId -> normalized app_name to detect duplicates
            const integrationIdToNormalizedName = new Map();
            allIntegrations.forEach(intg => {
              if (intg.id && intg.app_name) {
                integrationIdToNormalizedName.set(
                  String(intg.id),
                  normalizeString(intg.app_name)
                );
              }
            });

            // Create a set of integrationIds that are already in integrationItems
            const existingIntegrationIds = new Set(
              integrationItems.map(item => item.integrationId)
            );

            // Filter locationItems to remove duplicates of integration-level entries
            // A locationItem is considered a duplicate if:
            // 1. Its integrationId is already in integrationItems
            // 2. Its label (normalized) matches the integration's app_name (normalized)
            const deduplicatedLocationItems = locationItems.filter(locItem => {
              if (
                !locItem.integrationId ||
                !existingIntegrationIds.has(locItem.integrationId)
              ) {
                // Keep if no integrationId or if integration not in integrationItems
                return true;
              }

              // Check if this location's label matches the integration name
              const normalizedLabel = normalizeString(locItem.label);
              const normalizedIntegrationName =
                integrationIdToNormalizedName.get(locItem.integrationId);

              // Keep the location if its label doesn't match the integration name
              // (meaning it's a specific location, not a duplicate integration entry)
              return normalizedLabel !== normalizedIntegrationName;
            });

            // Show integrations first, then locations in the dropdown
            const finalItems = [
              ...integrationItems,
              ...deduplicatedLocationItems,
            ];

            if (locationItems.length > 0) {
              // Populate options; default select whichever is first in finalItems
              dispatch(setLocationData(finalItems));

              const firstOption = finalItems[0];
              const firstIntegrationId = firstOption?.integrationId;
              if (firstIntegrationId) {
                dispatch(setIntegration(firstIntegrationId));
              }

              // Set currency from the first option if available, else fallback to first location's currency
              if (firstOption?.currency) {
                dispatch(setCurrency(firstOption.currency));
              } else if (locData && locData[0]?.currency) {
                dispatch(setCurrency(locData[0].currency));
              }

              if (!multiple && !locationValue && firstOption) {
                setLocation(firstOption.value);
              }

              const needPeriodData =
                !defaultDateValue &&
                !(periodSwitcher && periodSelect) &&
                (!periodDataFetched ||
                  lastFetchedIntegration !== firstIntegrationId);

              if (needPeriodData) {
                setTimeout(() => fetchPeriod(), 10);
              }
            } else {
              // No locations: show integration-level options (filtered by privilege)
              dispatch(setLocationData(integrationItems));
              if (integrationItems.length > 0) {
                const firstIntegrationId = integrationItems[0].integrationId;
                dispatch(setIntegration(firstIntegrationId));
                if (!multiple && !locationValue) {
                  setLocation(integrationItems[0].value);
                }
                setTimeout(() => fetchPeriod(), 10);
              }
            }
          })
          .catch(err => {
            if (err.name !== 'AbortError') {
              console.error('Error fetching locations/integrations:', err);
            }
          });

        return () => controller.abort();
      }

      return () => {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      locationData?.length,
      locationValue,
      defaultDateValue,
      periodSwitcher,
      periodSelect,
      periodDataFetched,
      lastFetchedIntegration,
    ]);

    // Handle option selection
    const handleOptionSelect = useCallback(
      value => {
        if (multiple) {
          let newSelectedValues;

          if (selectedValues.includes(value)) {
            newSelectedValues = selectedValues.filter(val => val !== value);
          } else {
            newSelectedValues = [...selectedValues, value];
          }

          setSelectedValues(newSelectedValues);
          setLocation(newSelectedValues);
        } else {
          handleLocationSelect(value);
          setLocation(value);
          setIsOpen(false);
        }
      },
      [handleLocationSelect, setLocation, multiple, selectedValues]
    );

    // Effect hooks
    useEffect(() => {
      const cleanup = fetchLocations();
      return cleanup;
    }, [fetchLocations]);

    // Re-apply selection when the controlled locationValue changes
    useEffect(() => {
      if (!multiple && locationValue) {
        handleLocationSelect(locationValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationValue, multiple]);

    useEffect(() => {
      if (multiple && Array.isArray(locationValue)) {
        setSelectedValues(locationValue);
      }
    }, [locationValue, multiple]);

    return (
      <FormControl
        position="relative"
        ref={ref}
        width="100%"
        minW="200px"
        maxW="400px"
        mb={2}
      >
        {label && (
          <Text
            as="label"
            fontSize="sm"
            fontWeight="medium"
            color="gray.700"
            mb={2}
            display="block"
          >
            {label}
          </Text>
        )}
        {/* Selector Button - Styled exactly as in Figma */}
        <Box
          onClick={isDisabled ? undefined : () => setIsOpen(!isOpen)}
          bg={isDisabled ? 'gray.50' : 'gray.100'}
          borderRadius="full"
          border="none"
          px={5}
          py={2}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          minWidth="200px"
          width="100%"
          height={height}
          transition="all 0.2s ease"
          _hover={isDisabled ? {} : { bg: 'gray.200' }}
          overflow="hidden"
          opacity={isDisabled ? 0.6 : 1}
        >
          <Tooltip
            label={selectedLocationLabel}
            isDisabled={selectedLocationLabel.length <= 20}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            borderRadius="md"
            px={2}
            py={1}
          >
            <Text
              fontSize="14px"
              fontWeight="medium"
              color={isDisabled ? 'gray.400' : 'gray.800'}
              noOfLines={1}
              maxWidth="calc(100% - 32px)"
              isTruncated={false}
            >
              {selectedLocationLabel}
            </Text>
          </Tooltip>
          <Box flexShrink={0} ml={2}>
            <ChevronDownIcon
              color="gray.600"
              transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
              transition="transform 0.2s"
              boxSize={5}
            />
          </Box>
        </Box>

        {/* Dropdown Menu - Styled exactly as in Figma */}
        {isOpen && !isDisabled && (
          <Box
            position="absolute"
            top="calc(100% + 8px)"
            left={0}
            right={0}
            zIndex={20}
            bg="gray.50"
            borderRadius="16px"
            boxShadow="0px 4px 16px rgba(0, 0, 0, 0.08)"
            width="100%"
            maxHeight="320px"
            overflowY="auto"
          >
            {/* Search Input */}
            <Box p={3}>
              <InputGroup size="md">
                <Input
                  placeholder="Search locations"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  borderRadius="full"
                  fontSize="14px"
                  bg="white"
                  border="1px solid"
                  borderColor="blue.500"
                  _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                  _hover={{ borderColor: 'blue.500' }}
                  height="40px"
                  pl={10}
                />
                <InputRightElement height="40px" width="40px">
                  {searchValue ? (
                    <Box
                      as="button"
                      onClick={e => {
                        e.stopPropagation();
                        setSearchValue('');
                      }}
                      borderRadius="full"
                      bg="gray.200"
                      p={1}
                    >
                      <ChevronDownIcon
                        color="gray.600"
                        boxSize={4}
                        transform="rotate(45deg)"
                      />
                    </Box>
                  ) : null}
                </InputRightElement>
                <Box
                  position="absolute"
                  left="3"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                >
                  <SearchIcon color="gray.500" boxSize={4} />
                </Box>
              </InputGroup>
            </Box>

            {/* Options List */}
            <List spacing={0} py={2} px={2}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const isSelected = multiple
                    ? selectedValues?.includes(option.value)
                    : option.value === locationValue;

                  return (
                    <ListItem
                      key={option.value}
                      px={4}
                      py={3}
                      cursor="pointer"
                      _hover={{ bg: 'white' }}
                      onClick={() => handleOptionSelect(option.value)}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      bg={'transparent'}
                      transition="background 0.2s ease"
                      borderRadius="md"
                      mb={1}
                    >
                      <Text
                        fontSize="md"
                        fontWeight={isSelected ? 'medium' : 'normal'}
                        color={isSelected ? 'blue.500' : 'gray.800'}
                      >
                        {option.label}
                      </Text>
                      {isSelected && (
                        <CheckIcon color="#1C5DCD" width="24px" height="24px" />
                      )}
                    </ListItem>
                  );
                })
              ) : (
                <ListItem px={4} py={3}>
                  <Text fontSize="md" color="gray.500" textAlign="center">
                    No locations found
                  </Text>
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </FormControl>
    );
  }
);

export default CustomLocationSelector;
